import { useEffect, useLayoutEffect, useRef, type JSX } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import './guidance.css';

import { configureGuidanceQueue, useGuidance } from './api';
import { useStepContext } from './context';
import { GuidanceDeepLinks } from './deep-links';
import { useTourFeatures } from './features';
import { GuideHub } from './guide-hub';
import { HelpNotesDialog } from './help-notes-dialog';
import { HubSectionProbes } from './hub-probes';
import { guidanceQueue } from './queue';
import { setGuidanceFeatures, setGuidanceNavigator } from './runtime';
import { StruggleWatch } from './struggle/struggle-watch';
import { TourRunner } from './tour-runner';
import { useTourStore } from './tour-store';
import { FirstRun } from './welcome-dialog';

/**
 * Mounted once in `AppLayout`: the welcome, the tour runner, the hub, the
 * help notes, the `?help=` deep links, the struggle detector and the
 * event-queue flusher. Renders nothing itself.
 */
export function GuidanceRoot(): JSX.Element {
  return (
    <>
      <GuidanceSync />
      <QueueFlusher />
      <HubSectionProbes />
      <FirstRun />
      <TourRunner />
      <GuideHub />
      <HelpNotesDialog />
      <GuidanceDeepLinks />
      <StruggleWatch />
    </>
  );
}

/** Keeps the runtime's view of where the user is, who they are and what they may use. */
function GuidanceSync(): null {
  const location = useLocation();
  const navigate = useNavigate();
  const ctx = useStepContext();
  const features = useTourFeatures();
  const setLocation = useTourStore((s) => s.setLocation);
  const setEnv = useTourStore((s) => s.setEnv);

  useLayoutEffect(() => {
    setLocation(location.pathname, location.search);
  }, [location.pathname, location.search, setLocation]);

  useLayoutEffect(() => {
    setEnv(ctx.viewport, ctx.persona);
  }, [ctx.viewport, ctx.persona, setEnv]);

  useLayoutEffect(() => {
    setGuidanceFeatures(features);
  }, [features]);

  useLayoutEffect(() => {
    setGuidanceNavigator((to) => void navigate(to));
    return () => setGuidanceNavigator(null);
  }, [navigate]);

  return null;
}

/**
 * Attaches the event queue to this app's cache, and retries anything unsent
 * after the next successful guidance request or when the browser is back online.
 */
function QueueFlusher(): null {
  const queryClient = useQueryClient();
  const guidance = useGuidance();

  useLayoutEffect(() => {
    configureGuidanceQueue(queryClient);
  }, [queryClient]);

  // After a real GET (not our own cache writes, which also bump the data).
  const wasFetching = useRef(false);
  useEffect(() => {
    if (wasFetching.current && !guidance.isFetching && guidance.isSuccess) guidanceQueue.retry();
    wasFetching.current = guidance.isFetching;
  }, [guidance.isFetching, guidance.isSuccess]);

  useEffect(() => {
    const online = (): void => guidanceQueue.retry();
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, []);

  return null;
}
